import React from 'react';
import { updatePnlDropdowns, PnlLevel, fetchPnlYears } from '../pnl/api';
import Dropdowns from '../../components/Dropdowns';
import { StatsCard } from "components/ui/stats-card"
import { useBreadcrumbs } from 'contexts/BreadcrumbsContext';
import { useAuth } from 'contexts/AuthContext';
import { fetchCustomerAnalysisStats, CustomerAnalysisResponse, fetchCustomerDrilldown, CustomerTableRow, BucketData, fetchCustomerBranches, BranchTableRow, fetchBranchCn, CnTableRow } from './api';
import AnalysisTable from './components/AnalysisTable';
import { cn } from 'lib/utils';
import { StatsSkeleton } from 'components/ui/stats-skeleton';
import { useSmoothLoading } from 'lib/use-smooth-loading';

function useStableRef<T>(initial: T) {
  const ref = React.useRef(initial);
  return ref;
}

// Month data ordered Apr to Mar (financial year order)
const monthsData = [
  { value: '4', label: 'Apr', fullLabel: 'April' },
  { value: '5', label: 'May', fullLabel: 'May' },
  { value: '6', label: 'Jun', fullLabel: 'June' },
  { value: '7', label: 'Jul', fullLabel: 'July' },
  { value: '8', label: 'Aug', fullLabel: 'August' },
  { value: '9', label: 'Sep', fullLabel: 'September' },
  { value: '10', label: 'Oct', fullLabel: 'October' },
  { value: '11', label: 'Nov', fullLabel: 'November' },
  { value: '12', label: 'Dec', fullLabel: 'December' },
  { value: '1', label: 'Jan', fullLabel: 'January' },
  { value: '2', label: 'Feb', fullLabel: 'February' },
  { value: '3', label: 'Mar', fullLabel: 'March' },
];

// Narrow type used for drilldown requests (works for both buckets and total)
type DrillBucket = Pick<BucketData, 'title' | 'min_pct' | 'max_pct' | 'min_inclusive' | 'max_inclusive'>;

const CustomerAnalysisPage: React.FC = () => {
  const { setBreadcrumbs } = useBreadcrumbs();
  const { permissions } = useAuth();
  const [years, setYears] = React.useState<string[]>([]);
  const [year, setYear] = React.useState<string>('');
  const [levels, setLevels] = React.useState<PnlLevel[]>([]);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [error, setError] = React.useState<string>('');
  const [selectedMonths, setSelectedMonths] = React.useState<string[]>([]);
  const [analysisData, setAnalysisData] = React.useState<CustomerAnalysisResponse | null>(null);
  const [analysisLoading, setAnalysisLoading] = React.useState<boolean>(false);
  const [analysisError, setAnalysisError] = React.useState<string>('');
  
  // Smooth loading states
  const { isLoading: smoothAnalysisLoading, showContent: showAnalysisContent } = useSmoothLoading(analysisLoading, {
    minLoadingTime: 200,
    debounceTime: 200
  });
  
  // Customer table state
  const [showCustomerTable, setShowCustomerTable] = React.useState<boolean>(false);
  const [customerData, setCustomerData] = React.useState<CustomerTableRow[]>([]);
  const [customerTotal, setCustomerTotal] = React.useState<number>(0);
  const [customerLoading, setCustomerLoading] = React.useState<boolean>(false);
  const [customerError, setCustomerError] = React.useState<string>('');
  const [selectedBucket, setSelectedBucket] = React.useState<DrillBucket | null>(null);
  const [customerConversionType, setCustomerConversionType] = React.useState<'INR' | 'Lakhs' | 'Crores'>('INR');
  const [customerSort, setCustomerSort] = React.useState<{ by: string; dir: 'asc' | 'desc' }>({ by: 'customer_name', dir: 'asc' });
  const [customerPage, setCustomerPage] = React.useState<number>(1);
  const [customerPageSize] = React.useState<number>(20);
  const [customerHasLoadedOnce, setCustomerHasLoadedOnce] = React.useState(false);

  // Branch table state
  const [showBranchTable, setShowBranchTable] = React.useState<boolean>(false);
  const [branchData, setBranchData] = React.useState<BranchTableRow[]>([]);
  const [branchLoading, setBranchLoading] = React.useState<boolean>(false);
  const [branchError, setBranchError] = React.useState<string>('');
  const [selectedCustomer, setSelectedCustomer] = React.useState<{ code: string; name: string } | null>(null);
  const [branchConversionType, setBranchConversionType] = React.useState<'INR' | 'Lakhs' | 'Crores'>('INR');
  const [branchSort, setBranchSort] = React.useState<{ by: string; dir: 'asc' | 'desc' }>({ by: 'branch_name', dir: 'asc' });
  const [branchPage, setBranchPage] = React.useState<number>(1);
  const [branchPageSize] = React.useState<number>(20);
  const [branchHasLoadedOnce, setBranchHasLoadedOnce] = React.useState(false); 
  
  // CN table state
  const [showCnTable, setShowCnTable] = React.useState<boolean>(false);
  const [cnData, setCnData] = React.useState<CnTableRow[]>([]);
  const [cnLoading, setCnLoading] = React.useState<boolean>(false);
  const [cnError, setCnError] = React.useState<string>('');
  const [selectedBranch, setSelectedBranch] = React.useState<{ ouCode: string; branchName: string } | null>(null);
  const [cnConversionType, setCnConversionType] = React.useState<'INR' | 'Lakhs' | 'Crores'>('INR');
  const [cnSort, setCnSort] = React.useState<{ by: string; dir: 'asc' | 'desc' }>({ by: 'cn_number', dir: 'asc' });
  const [cnPage, setCnPage] = React.useState<number>(1);
  const [cnPageSize] = React.useState<number>(20);
  const [cnHasLoadedOnce, setCnHasLoadedOnce] = React.useState(false);
  const [cnTotal, setCnTotal] = React.useState<number>(0); 
  
  const didInitRef = useStableRef(false);

  // Permission check helper
  const hasCnPermission = React.useMemo(() => {
    return permissions.includes('cn.data.view');
  }, [permissions]);

  const fetchLevels = React.useCallback(async (selectionMap?: Record<string, string[]>) => {
    if (!year) return;
    setLoading(true);
    setError('');
    try {
      const data = await updatePnlDropdowns({ year, selection: selectionMap || {} });
      setLevels(Array.isArray(data?.levels) ? data.levels : []);
    } catch (err: unknown) {
      const isForbidden = (err as any)?.status === 403;
      const message = err instanceof Error ? err.message : isForbidden ? 'You are not allowed to access this resource.' : 'Something went wrong';
      setError(message);
      if (isForbidden) setLevels([]);
    } finally {
      setLoading(false);
    }
  }, [year]);

  const buildFiltersFromLevels = React.useCallback((currentLevels: PnlLevel[]) => {
    const filters: Record<string, string[]> = {};
    for (const level of currentLevels) {
      if (level && level.selected && level.selected.length > 0) {
        filters[String(level.id)] = level.selected;
      }
    }
    return filters;
  }, []);

  const fetchAnalysisData = React.useCallback(async (currentLevels: PnlLevel[], months: string[]) => {
    if (!year) return;
    const filters = buildFiltersFromLevels(currentLevels);
    const monthsToSend = months.length > 0 ? months.map(m => parseInt(m)) : undefined;

    setAnalysisLoading(true);
    setAnalysisError('');
    try {
      const data = await fetchCustomerAnalysisStats({
        year,
        filters,
        ...(monthsToSend && { months: monthsToSend })
      });
      setAnalysisData(data);
    } catch (err: unknown) {
      const isForbidden = (err as any)?.status === 403;
      const message = err instanceof Error ? err.message : isForbidden ? 'You are not allowed to access this resource.' : 'Failed to fetch analysis data';
      setAnalysisError(message);
      if (isForbidden) setAnalysisData(null);
    } finally {
      setAnalysisLoading(false);
    }
  }, [buildFiltersFromLevels, year]);

  React.useEffect(() => {
    setBreadcrumbs([
      { label: 'Dashboard', href: '/dashboard/customer-analysis' },
      { label: 'Customer Analysis' },
    ]);
    if (didInitRef.current) return;
    didInitRef.current = true;
    (async () => {
      try {
        const resp = await fetchPnlYears();
        const yrList = Array.isArray(resp?.years) ? resp.years : [];
        setYears(yrList);
        setYear(yrList[0] || '');
      } catch (e) {
        setYears([]);
        setYear('');
      }
    })();
  }, [didInitRef, setBreadcrumbs]);

  React.useEffect(() => {
    if (!year) return;
    setLevels([]);
    setAnalysisData(null);

    setCustomerHasLoadedOnce(false);
    setBranchHasLoadedOnce(false);
    setCnHasLoadedOnce(false);
    setCnTotal(0);

    fetchLevels({});
    fetchAnalysisData([], selectedMonths);
    
    // Clear all table states first
    setShowBranchTable(false);
    setShowCnTable(false);
    setSelectedCustomer(null);
    setSelectedBranch(null);
    setBranchData([]);
    setCnData([]);
    setBranchError('');
    setCnError('');
    
    // Load total table by default
    const totalBucket: DrillBucket = {
      title: 'Total',
      min_pct: null,
      max_pct: null,
      min_inclusive: false,
      max_inclusive: false,
    };
    setSelectedBucket(totalBucket);
    setShowCustomerTable(true);
    setCustomerPage(1);
    // fetchCustomerData will be called by the unified useEffect
  }, [year, fetchLevels, fetchAnalysisData]);

  const buildSelectionFromLevels = React.useCallback((currentLevels: PnlLevel[]) => {
    const map: Record<string, string[]> = {};
    for (const level of currentLevels) {
      if (level && level.selected && level.selected.length > 0) {
        map[String(level.id)] = level.selected;
      }
    }
    return map;
  }, []);

  const handleSelectChange = React.useCallback((levelId: number | string, value: string[]) => {
    const desc = (levels || [])
      .filter((l) => Boolean(l?.visible))
      .sort((a, b) => Number(b.id) - Number(a.id));
    const indexById = Object.fromEntries(desc.map((l, i) => [String(l.id), i]));
    const changedIdx = indexById[String(levelId)];

    const nextLevels = levels.map((lvl) => {
      const idx = indexById[String(lvl.id)];
      if (idx < changedIdx) {
        return lvl.id === levelId ? { ...lvl, selected: value.length > 0 ? value : null } : lvl;
      }
      if (idx === changedIdx) {
        return { ...lvl, selected: value.length > 0 ? value : null };
      }
      return { ...lvl, selected: null };
    });

    setLevels(nextLevels);
    const selection = buildSelectionFromLevels(nextLevels);
    if (value.length === 0) delete selection[String(levelId)];
    fetchLevels(selection);
    fetchAnalysisData(nextLevels, selectedMonths);
  }, [levels, buildSelectionFromLevels, fetchLevels, fetchAnalysisData, selectedMonths]);

  const handleYearChange = React.useCallback((newYear: string) => {
    if (!newYear) return;
    setYear(newYear);
  }, []);

  const handleBatchSelect = React.useCallback((updates: Array<{ levelId: number | string; value: string[] }>) => {
    if (!Array.isArray(updates) || updates.length === 0) return;

    const updatesById = Object.fromEntries(updates.map(u => [String(u.levelId), u.value]));

    const nextLevels = (levels || []).map((lvl) => {
      const updated = updatesById[String(lvl.id)];
      if (updated) {
        return { ...lvl, selected: updated };
      }
      return lvl;
    });

    setLevels(nextLevels);
    const selection = buildSelectionFromLevels(nextLevels);
    fetchLevels(selection);
    fetchAnalysisData(nextLevels, selectedMonths);
  }, [levels, buildSelectionFromLevels, fetchLevels, fetchAnalysisData, selectedMonths]);

  const handleMonthsChange = React.useCallback((months: string[]) => {
    setSelectedMonths(months);
    fetchAnalysisData(levels, months);
  }, [levels, fetchAnalysisData]);

  const fetchCustomerData = React.useCallback(async (bucket: DrillBucket) => {
    if (!year) return;
    const filters = buildFiltersFromLevels(levels);
    const monthsToSend = selectedMonths.length > 0 ? selectedMonths.map(m => parseInt(m)) : undefined;

    setCustomerLoading(true);
    setCustomerError('');
    try {
      const data = await fetchCustomerDrilldown({
        year,
        filters,
        ...(monthsToSend && { months: monthsToSend }),
        bucket: {
          min_pct: bucket.min_pct,
          max_pct: bucket.max_pct,
          min_inclusive: bucket.min_inclusive,
          max_inclusive: bucket.max_inclusive,
        },
        sort: {
          by: customerSort.by as any,
          dir: customerSort.dir,
        },
        page: customerPage,
        page_size: customerPageSize,
      });
      setCustomerData(data.items);
      setCustomerTotal(data.total);
    } catch (err: unknown) {
      const isForbidden = (err as any)?.status === 403;
      const message = err instanceof Error ? err.message : isForbidden ? 'You are not allowed to access this resource.' : 'Failed to fetch customer data';
      setCustomerError(message);
      if (isForbidden) {
        setCustomerData([]);
        setCustomerTotal(0);
      }
    } finally {
      setCustomerLoading(false);
      setCustomerHasLoadedOnce(true);
    }
  }, [buildFiltersFromLevels, levels, selectedMonths, year, customerSort.by, customerSort.dir, customerPage, customerPageSize]);

  const handleTotalClick = React.useCallback(() => {
    const totalBucket: DrillBucket = {
      title: 'Total',
      min_pct: null,
      max_pct: null,
      min_inclusive: false,
      max_inclusive: false,
    };
    // Clear all other table states first
    setShowBranchTable(false);
    setShowCnTable(false);
    setSelectedCustomer(null);
    setSelectedBranch(null);
    setBranchData([]);
    setCnData([]);
    setBranchError('');
    setCnError('');
    
    // Then set customer table
    setSelectedBucket(totalBucket);
    setShowCustomerTable(true);
    setCustomerPage(1);
  }, []);

  const handleBucketClick = React.useCallback((bucket: DrillBucket) => {
    // Clear all other table states first
    setShowBranchTable(false);
    setShowCnTable(false);
    setSelectedCustomer(null);
    setSelectedBranch(null);
    setBranchData([]);
    setCnData([]);
    setBranchError('');
    setCnError('');
    
    // Then set customer table
    setSelectedBucket(bucket);
    setShowCustomerTable(true);
    setCustomerPage(1); // Reset to first page
  }, []);

  const handleCloseCustomerTable = React.useCallback(() => {
    // Clear all table states
    setShowCustomerTable(false);
    setShowBranchTable(false);
    setShowCnTable(false);
    setSelectedBucket(null);
    setSelectedCustomer(null);
    setSelectedBranch(null);
    setCustomerData([]);
    setBranchData([]);
    setCnData([]);
    setCustomerTotal(0);
    setCustomerError('');
    setBranchError('');
    setCnError('');
    setCnTotal(0);
  }, []);

  const handleCustomerSort = React.useCallback((by: string, dir: 'asc' | 'desc') => {
    setCustomerSort({ by, dir });
    setCustomerPage(1); // Reset to first page
  }, []);

  const handleCustomerPageChange = React.useCallback((page: number) => {
    setCustomerPage(page);
  }, []);

  const fetchBranchData = React.useCallback(async (customerCode: string) => {
    if (!year || !customerCode) return;
    const filters = buildFiltersFromLevels(levels);
    const monthsToSend = selectedMonths.length > 0 ? selectedMonths.map(m => parseInt(m)) : undefined;

    setBranchLoading(true);
    setBranchError('');
    try {
      const data = await fetchCustomerBranches({
        year,
        filters,
        ...(monthsToSend && { months: monthsToSend }),
        customer_code: customerCode,
        sort: {
          by: branchSort.by as any,
          dir: branchSort.dir,
        },
        page: branchPage,
        page_size: branchPageSize,
      });
      setBranchData(data.items);
    } catch (err: unknown) {
      const isForbidden = (err as any)?.status === 403;
      const message = err instanceof Error ? err.message : isForbidden ? 'You are not allowed to access this resource.' : 'Failed to fetch branch data';
      setBranchError(message);
      if (isForbidden) {
        setBranchData([]);
      }
    } finally {
      setBranchLoading(false);
      setBranchHasLoadedOnce(true);
    }
  }, [buildFiltersFromLevels, levels, selectedMonths, year, branchSort.by, branchSort.dir, branchPage, branchPageSize]);

  const handleCustomerClick = React.useCallback((customerCode: string, customerName: string) => {
    // Clear CN state first
    setShowCnTable(false);
    setSelectedBranch(null);
    setCnData([]);
    setCnError('');
    
    // Set branch state
    setSelectedCustomer({ code: customerCode, name: customerName });
    setShowBranchTable(true);
    setShowCustomerTable(false);
    setBranchPage(1);
  }, []);

  const handleBackToCustomers = React.useCallback(() => {
    // Clear branch and CN states
    setShowBranchTable(false);
    setShowCnTable(false);
    setSelectedCustomer(null);
    setSelectedBranch(null);
    setBranchData([]);
    setCnData([]);
    setBranchError('');
    setCnError('');
    
    // Show customer table
    setShowCustomerTable(true);
  }, []);

  const handleBranchSort = React.useCallback((by: string, dir: 'asc' | 'desc') => {
    setBranchSort({ by, dir });
    setBranchPage(1);
  }, []);

  const handleBranchPageChange = React.useCallback((page: number) => {
    setBranchPage(page);
  }, []);

  const fetchCnData = React.useCallback(async (customerCode: string, ouCode: string) => {
    if (!year || !customerCode || !ouCode) return;
    const filters = buildFiltersFromLevels(levels);
    const monthsToSend = selectedMonths.length > 0 ? selectedMonths.map(m => parseInt(m)) : undefined;

    setCnLoading(true);
    setCnError('');
    try {
      const data = await fetchBranchCn({
        year,
        filters,
        ...(monthsToSend && { months: monthsToSend }),
        customer_code: customerCode,
        ou_code: ouCode,
        sort: {
          by: cnSort.by as any,
          dir: cnSort.dir,
        },
        page: cnPage,
        page_size: cnPageSize,
      });
      setCnData(data.items);
      setCnTotal(data.total);
    } catch (err: unknown) {
      const isForbidden = (err as any)?.status === 403;
      const message = err instanceof Error ? err.message : isForbidden ? 'You are not allowed to access this resource.' : 'Failed to fetch CN data';
      setCnError(message);
      if (isForbidden) {
        setCnData([]);
        setCnTotal(0);
      }
    } finally {
      setCnLoading(false);
      setCnHasLoadedOnce(true);
    }
  }, [buildFiltersFromLevels, levels, selectedMonths, year, cnSort.by, cnSort.dir, cnPage, cnPageSize]);

  const handleBranchClick = React.useCallback((ouCode: string, branchName: string) => {
    if (!hasCnPermission) return;
    setSelectedBranch({ ouCode, branchName });
    setShowCnTable(true);
    setShowBranchTable(false);
    setCnPage(1);
  }, [hasCnPermission]);

  const handleBackToBranches = React.useCallback(() => {
    setShowCnTable(false);
    setShowBranchTable(true);
    setSelectedBranch(null);
    setCnData([]);
    setCnError('');
    setCnTotal(0);
  }, []);

  const handleCnSort = React.useCallback((by: string, dir: 'asc' | 'desc') => {
    setCnSort({ by, dir });
    setCnPage(1);
  }, []);

  const handleCnPageChange = React.useCallback((page: number) => {
    setCnPage(page);
  }, []);

  // Single effect to handle all customer data refetching
  React.useEffect(() => {
    if (!year) return;
    if (!selectedBucket || !showCustomerTable) return;
    if (levels.length === 0) return;

    fetchCustomerData(selectedBucket);
  
  }, [year, customerSort, customerPage, levels, selectedMonths, fetchCustomerData, selectedBucket, showCustomerTable]);

  // Effect to handle branch data refetching
  React.useEffect(() => {
    if (selectedCustomer && showBranchTable) {
      fetchBranchData(selectedCustomer.code);
    }
  }, [branchSort, branchPage, levels, selectedMonths, fetchBranchData, selectedCustomer, showBranchTable]);

  // Effect to handle CN data refetching
  React.useEffect(() => {
    if (selectedCustomer && selectedBranch && showCnTable) {
      fetchCnData(selectedCustomer.code, selectedBranch.ouCode);
    }
  }, [cnSort, cnPage, levels, selectedMonths, fetchCnData, selectedCustomer, selectedBranch, showCnTable]);

  return (
    <div className="space-y-6 mx-auto w-full max-w-screen-2xl 2xl:max-w-[2000px] px-4 sm:px-6">
      {/* Filters Section */}
      <div className="space-y-4">
        <Dropdowns
          year={year}
          years={years}
          levels={levels}
          loading={loading}
          onSelectChange={handleSelectChange}
          onBatchSelect={handleBatchSelect}
          onYearChange={handleYearChange}
        />

        {/* Month Selector Pills */}
        <div className="space-y-3">
          <div className="flex flex-wrap gap-2">
            {monthsData.map((month) => {
              const isSelected = selectedMonths.includes(month.value);
              return (
                <button
                  key={month.value}
                  type="button"
                  disabled={loading}
                  onClick={() => {
                    const newSelection = isSelected
                      ? selectedMonths.filter(m => m !== month.value)
                      : [...selectedMonths, month.value];
                    handleMonthsChange(newSelection);
                  }}
                  className={cn(
                    "px-3 py-1.5 text-sm font-medium rounded-full border transition-colors",
                    "focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1",
                    isSelected
                      ? "bg-blue-100 border-blue-300 text-blue-800 hover:bg-blue-200"
                      : "bg-white border-gray-300 text-gray-700 hover:bg-gray-50",
                    loading && "opacity-50 cursor-not-allowed"
                  )}
                  title={month.fullLabel}
                >
                  {month.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Analysis Cards Section */}
      {smoothAnalysisLoading && (
        <div className="space-y-6">
          <StatsSkeleton count={6} />
        </div>
      )}

      {(error || analysisError || customerError) && !smoothAnalysisLoading && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-md">
          <p className="text-red-600 text-sm">
            {error || analysisError || customerError}
          </p>
        </div>
      )}

      {analysisData && showAnalysisContent && (
        <div className={`space-y-6 transition-opacity duration-300 ${showAnalysisContent ? 'opacity-100' : 'opacity-0'}`}>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
            {/* Total */}
            <StatsCard
              title={analysisData.cards.total.title}
              count={analysisData.cards.total.metrics.customers}
              cm={analysisData.cards.total.metrics.cm}
              cmPct={analysisData.cards.total.metrics.cm_pct}
              isTotal
              onClick={handleTotalClick}
            />

            {/* Buckets */}
            {analysisData.cards.buckets
              .sort((a, b) => a.order - b.order)
              .map((b) => (
                <StatsCard
                  key={b.title}
                  title={b.title}
                  count={b.metrics.customers}
                  cm={b.metrics.cm}
                  cmPct={b.metrics.cm_pct}
                  onClick={() => handleBucketClick(b)}
                />
              ))}
          </div>
        </div>
      )}

      {/* Customer Table Section */}
      {showCustomerTable && (
        <div className="space-y-4">
          <AnalysisTable
            data={customerData}
            total={customerTotal}
            loading={customerLoading}
            conversionType={customerConversionType}
            onConversionChange={setCustomerConversionType}
            sortBy={customerSort.by}
            sortDir={customerSort.dir}
            onSort={handleCustomerSort}
            page={customerPage}
            pageSize={customerPageSize}
            onPageChange={handleCustomerPageChange}
            onClose={handleCloseCustomerTable}
            bucketTitle={selectedBucket?.title}
            onCustomerClick={handleCustomerClick}
            hasLoadedOnce={customerHasLoadedOnce}
          />
        </div>
      )}

      {/* Branch Table Section */}
      {showBranchTable && selectedCustomer && (
        <div className="space-y-4">
          {branchError && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-red-600 text-sm">{branchError}</p>
            </div>
          )}

          <AnalysisTable
            data={branchData}
            total={branchData.length}
            loading={branchLoading}
            conversionType={branchConversionType}
            onConversionChange={setBranchConversionType}
            sortBy={branchSort.by}
            sortDir={branchSort.dir}
            onSort={handleBranchSort}
            page={branchPage}
            pageSize={branchPageSize}
            onPageChange={handleBranchPageChange}
            onClose={handleBackToCustomers}
            bucketTitle={`${selectedCustomer.name}`}
            isBranchView={true}
            onBranchClick={handleBranchClick}
            hasCnPermission={hasCnPermission}
            hasLoadedOnce={branchHasLoadedOnce}
          />
        </div>
      )}

      {/* CN Table Section */}
      {showCnTable && selectedCustomer && selectedBranch && (
        <div className="space-y-4">
          {cnError && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-red-600 text-sm">{cnError}</p>
            </div>
          )}

          <AnalysisTable
            data={cnData}
            total={cnTotal}
            loading={cnLoading}
            conversionType={cnConversionType}
            onConversionChange={setCnConversionType}
            sortBy={cnSort.by}
            sortDir={cnSort.dir}
            onSort={handleCnSort}
            page={cnPage}
            pageSize={cnPageSize}
            onPageChange={handleCnPageChange}
            onClose={handleBackToBranches}
            bucketTitle={`${selectedBranch.branchName}`}
            isCnView={true}
            hasLoadedOnce={cnHasLoadedOnce}
          />
        </div>
      )}
    </div>
  );
};

export default CustomerAnalysisPage;
